import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmailDetails, VerificationHistory } from '@/types';
import { formatYesNoUnknown, formatYesNoUnknownInverse, getStatusBadge } from '@/lib/email-formatters';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Mail,
  Globe,
  Server,
  Clock,
  Shield,
  AlertTriangle,
  Code,
  History,
  Database,
  Loader2,
} from 'lucide-react';

interface DailyStats {
  date: string;
  total_requests: number;
  cache_hits: number;
}

interface Domain {
  domain: string;
  count: number;
  deliverable: number;
  undeliverable: number;
}

interface Email {
  id: string;
  email: string;
  status: string;
  domain: string;
  verified_at: string;
  projects: Array<{ id: string; name: string }>;
}

export default function Stats() {
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [topDomains, setTopDomains] = useState<Domain[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [emailsTotal, setEmailsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    domain: '',
    page: 1,
  });
  const [selectedEmail, setSelectedEmail] = useState<EmailDetails | null>(null);
  const [emailHistory, setEmailHistory] = useState<VerificationHistory[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const to = new Date().toISOString().split('T')[0];
        const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        const [dailyData, domainsData] = await Promise.all([
          api.getDailyStats(from, to),
          api.getTopDomains(),
        ]);

        setDailyStats(dailyData.stats.reverse());
        setTopDomains(domainsData.domains);
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const apiFilters = {
          ...filters,
          status: filters.status === 'all' ? '' : filters.status,
        };
        const result = await api.getEmails(apiFilters);
        setEmails(result.emails);
        setEmailsTotal(result.total);
      } catch {
        // Silently fail
      }
    };
    fetchEmails();
  }, [filters]);

  const openEmailDetails = async (emailId: string) => {
    setSheetOpen(true);
    setLoadingDetails(true);
    try {
      const data = await api.getEmailDetails(emailId);
      setSelectedEmail(data.email);
      setEmailHistory(data.history);
    } catch {
      // Silently fail
    } finally {
      setLoadingDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Statistics</h1>
        <p className="text-muted-foreground">Analytics and email verification data</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Daily Requests (Last 30 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {dailyStats.length === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No data yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString('en', { month: 'short', day: 'numeric' })
                    }
                    className="text-muted-foreground"
                  />
                  <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
                  <Tooltip
                    labelFormatter={(v) => new Date(v).toLocaleDateString()}
                    formatter={(v: number) => [v.toLocaleString(), '']}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="total_requests"
                    name="Total"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="cache_hits"
                    name="Cache Hits"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Domains</CardTitle>
          </CardHeader>
          <CardContent>
            {topDomains.length === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No data yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topDomains.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="domain" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip
                    formatter={(v: number) => [v.toLocaleString(), '']}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem',
                    }}
                  />
                  <Bar dataKey="deliverable" name="Deliverable" fill="#10b981" stackId="a" />
                  <Bar dataKey="undeliverable" name="Undeliverable" fill="#ef4444" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Emails</CardTitle>
            <div className="flex items-center gap-2">
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value, page: 1 })}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="deliverable">Deliverable</SelectItem>
                  <SelectItem value="risky">Risky</SelectItem>
                  <SelectItem value="undeliverable">Undeliverable</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by domain"
                  className="pl-9 w-[200px]"
                  value={filters.domain}
                  onChange={(e) => setFilters({ ...filters, domain: e.target.value, page: 1 })}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {emails.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              No emails found
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Projects</TableHead>
                    <TableHead>Verified</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emails.map((e) => (
                    <TableRow
                      key={e.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => openEmailDetails(e.id)}
                    >
                      <TableCell className="font-mono text-sm">{e.email}</TableCell>
                      <TableCell>{getStatusBadge(e.status)}</TableCell>
                      <TableCell>{e.domain}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {e.projects && e.projects.length > 0 ? (
                            e.projects.map((p) => (
                              <Badge key={p.id} variant="secondary">
                                {p.name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(e.verified_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <span className="text-sm text-muted-foreground">
                  Total: {emailsTotal.toLocaleString()} emails
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={filters.page === 1}
                    onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">Page {filters.page}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={emails.length < 50}
                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Details
            </SheetTitle>
            <SheetDescription>
              {selectedEmail?.email}
            </SheetDescription>
          </SheetHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : selectedEmail ? (
            <Tabs defaultValue="overview" className="mt-6">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
                <TabsTrigger value="raw">Raw JSON</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4 space-y-6">
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Verification Result
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground mb-1">Status</p>
                      <div>{getStatusBadge(selectedEmail.status)}</div>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground mb-1">Reason</p>
                      <p className="font-mono text-sm">{selectedEmail.reason || '-'}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground mb-1">Score</p>
                      <p className="text-lg font-bold">
                        {selectedEmail.score !== null ? `${selectedEmail.score}%` : '-'}
                      </p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground mb-1">Provider</p>
                      <p className="font-mono text-sm">{selectedEmail.provider || '-'}</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Domain Info
                  </h4>
                  <div className="rounded-lg border overflow-hidden">
                    <div className="grid grid-cols-2 gap-px bg-muted">
                      <div className="bg-background p-3">
                        <p className="text-xs text-muted-foreground mb-1">Domain</p>
                        <p className="font-mono text-sm">{selectedEmail.domain_name || '-'}</p>
                      </div>
                      <div className="bg-background p-3">
                        <p className="text-xs text-muted-foreground mb-1">Accept All</p>
                        {formatYesNoUnknown(selectedEmail.domain_accept_all)}
                      </div>
                      <div className="bg-background p-3">
                        <p className="text-xs text-muted-foreground mb-1">Disposable</p>
                        {formatYesNoUnknown(selectedEmail.domain_disposable)}
                      </div>
                      <div className="bg-background p-3">
                        <p className="text-xs text-muted-foreground mb-1">Free Provider</p>
                        {formatYesNoUnknownInverse(selectedEmail.domain_free)}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Account Info
                  </h4>
                  <div className="rounded-lg border overflow-hidden">
                    <div className="grid grid-cols-3 gap-px bg-muted">
                      <div className="bg-background p-3">
                        <p className="text-xs text-muted-foreground mb-1">Role Account</p>
                        {formatYesNoUnknown(selectedEmail.account_role)}
                      </div>
                      <div className="bg-background p-3">
                        <p className="text-xs text-muted-foreground mb-1">Disabled</p>
                        {formatYesNoUnknown(selectedEmail.account_disabled)}
                      </div>
                      <div className="bg-background p-3">
                        <p className="text-xs text-muted-foreground mb-1">Full Mailbox</p>
                        {formatYesNoUnknown(selectedEmail.account_full_mailbox)}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    DNS Info
                  </h4>
                  <div className="rounded-lg border p-3 space-y-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Type</p>
                      <p className="font-mono text-sm">{selectedEmail.dns_type || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Record</p>
                      <p className="font-mono text-xs break-all">{selectedEmail.dns_record || '-'}</p>
                    </div>
                  </div>
                </div>

                {(selectedEmail.toxic || selectedEmail.toxicity !== null) && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Toxicity
                      </h4>
                      <div className="rounded-lg border overflow-hidden">
                        <div className="grid grid-cols-2 gap-px bg-muted">
                          <div className="bg-background p-3">
                            <p className="text-xs text-muted-foreground mb-1">Type</p>
                            <p className="font-mono text-sm">{selectedEmail.toxic || '-'}</p>
                          </div>
                          <div className="bg-background p-3">
                            <p className="text-xs text-muted-foreground mb-1">Score</p>
                            <p className="font-mono text-sm">
                              {selectedEmail.toxicity !== null ? selectedEmail.toxicity : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <Separator />

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Metadata
                  </h4>
                  <div className="rounded-lg border p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Verified At</span>
                      <span className="font-mono">
                        {new Date(selectedEmail.verified_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Expires At</span>
                      <span className="font-mono">
                        {selectedEmail.expires_at
                          ? new Date(selectedEmail.expires_at).toLocaleString()
                          : 'Never'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Source</span>
                      <span className="font-mono">{selectedEmail.verification_source}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Verifications</span>
                      <span className="font-mono">{selectedEmail.verification_count}</span>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Verification History
                  </h4>
                  {emailHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No history available</p>
                  ) : (
                    <div className="space-y-2">
                      {emailHistory.map((h) => (
                        <div key={h.id} className="rounded-lg border p-3 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{h.project_name || 'Unknown'}</span>
                            <Badge variant={h.cached ? 'secondary' : 'outline'} className="text-xs">
                              {h.cached ? 'Cached' : 'Fresh'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>{new Date(h.created_at).toLocaleString()}</span>
                            {h.response_time_ms !== null && (
                              <span>{h.response_time_ms}ms</span>
                            )}
                          </div>
                          {h.api_key_name && (
                            <p className="text-xs text-muted-foreground">
                              API Key: {h.api_key_name}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="raw" className="mt-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Raw API Response
                  </h4>
                  {selectedEmail.raw_response ? (
                    <pre className="rounded-lg border bg-muted p-4 text-xs overflow-x-auto max-h-96">
                      {JSON.stringify(selectedEmail.raw_response, null, 2)}
                    </pre>
                  ) : (
                    <div className="rounded-lg border p-4 text-sm text-muted-foreground flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      No raw response cached for this email
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
